import React, { useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import {
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Tab,
  Tabs,
  Avatar,
  FormControlLabel,
  Checkbox,
  Link,
  Divider,
  IconButton,
  InputAdornment
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Videocam as VideoChatIcon
} from '@mui/icons-material';

const useStyles = makeStyles()((theme) => ({
  authContainer: {
    minHeight: '100vh',
    backgroundColor: '#0f1115',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(2),
    backgroundImage: 'linear-gradient(135deg, #0f1115 0%, #1a1f2e 50%, #252b3d 100%)',
  },
  authPaper: {
    width: '100%',
    maxWidth: 400,
    padding: theme.spacing(4),
    backgroundColor: '#1a1f2e',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: theme.spacing(2),
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
  },
  logoSection: {
    textAlign: 'center',
    marginBottom: theme.spacing(3),
  },
  logoIcon: {
    width: 60,
    height: 60,
    backgroundColor: theme.palette.primary.main,
    color: '#000000',
    fontSize: '2rem',
    marginBottom: theme.spacing(2),
  },
  logoText: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
  },
  logoSubtext: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
  },
  tabs: {
    marginBottom: theme.spacing(3),
    '& .MuiTabs-indicator': {
      backgroundColor: theme.palette.primary.main,
    },
  },
  tab: {
    fontSize: '0.875rem',
    fontWeight: 600,
    textTransform: 'none',
    color: theme.palette.text.secondary,
    '&.Mui-selected': {
      color: theme.palette.primary.main,
    },
  },
  formField: {
    marginBottom: theme.spacing(2),
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#0f1115',
      '& fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.2)',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.3)',
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
    '& .MuiOutlinedInput-input': {
      color: theme.palette.text.primary,
      '&::placeholder': {
        color: theme.palette.text.secondary,
        opacity: 0.7,
      },
    },
    '& .MuiInputLabel-root': {
      color: theme.palette.text.secondary,
      '&.Mui-focused': {
        color: theme.palette.primary.main,
      },
    },
  },
  submitButton: {
    width: '100%',
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.primary.main,
    color: '#000000',
    fontWeight: 600,
    fontSize: '1rem',
    marginBottom: theme.spacing(2),
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
    '&:disabled': {
      backgroundColor: 'rgba(0, 217, 255, 0.3)',
      color: 'rgba(0, 0, 0, 0.5)',
    },
  },
  guestButton: {
    width: '100%',
    padding: theme.spacing(1.5),
    backgroundColor: 'transparent',
    color: theme.palette.text.primary,
    fontWeight: 500,
    fontSize: '0.875rem',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    marginBottom: theme.spacing(2),
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      borderColor: 'rgba(255, 255, 255, 0.3)',
    },
  },
  formRow: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  halfField: {
    flex: 1,
  },
  checkboxLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    '& .MuiCheckbox-root': {
      color: theme.palette.text.secondary,
      '&.Mui-checked': {
        color: theme.palette.primary.main,
      },
    },
  },
  linkText: {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    fontSize: '0.875rem',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  divider: {
    margin: theme.spacing(2, 0),
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: '0.875rem',
    marginBottom: theme.spacing(2),
    textAlign: 'center',
  },
  successText: {
    color: theme.palette.secondary.main,
    fontSize: '0.875rem',
    marginBottom: theme.spacing(2),
    textAlign: 'center',
  },
}));

const AuthScreen = ({ onLogin, onGuestLogin, tenantConfig }) => {
  const { classes } = useStyles();
  const [activeTab, setActiveTab] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Tenant sign-in toggles. Default ON — only an explicit false hides the
  // option, so a config that hasn't loaded (or failed to) leaves everything
  // visible and the server's 403 is the backstop.
  const guestLoginEnabled = tenantConfig?.features?.guestLogin !== false;
  const registrationEnabled = tenantConfig?.features?.userRegistration !== false;
  // Sign Up gone → the login form is the only form, whatever tab was last picked.
  const showRegister = registrationEnabled && activeTab === 1;

  // Login form state
  const [loginForm, setLoginForm] = useState({
    username: '',
    password: '',
    rememberMe: false,
  });

  // Register form state  
  const [registerForm, setRegisterForm] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false,
  });

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setError('');
    setSuccess('');
  };

  const handleLoginChange = (field) => (event) => {
    setLoginForm({
      ...loginForm,
      [field]: event.target.type === 'checkbox' ? event.target.checked : event.target.value,
    });
  };

  const handleRegisterChange = (field) => (event) => {
    setRegisterForm({
      ...registerForm,
      [field]: event.target.type === 'checkbox' ? event.target.checked : event.target.value,
    });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validate form
      if (!loginForm.username || !loginForm.password) {
        throw new Error('Please fill in all fields');
      }

      // Call login function
      if (onLogin) {
        await onLogin(loginForm);
      }
      
      setSuccess('Login successful!');
    } catch (err) {
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validate form
      if (!registerForm.username || !registerForm.password) {
        throw new Error('Please fill in username and password');
      }

      if (registerForm.password !== registerForm.confirmPassword) {
        throw new Error('Passwords do not match');
      }

      if (registerForm.password.length < 6) {
        throw new Error('Password must be at least 6 characters');
      }

      if (!registerForm.agreeToTerms) {
        throw new Error('Please agree to the terms and conditions');
      }

      // Call register function (would be passed as prop)
      if (onLogin) {
        await onLogin({
          ...registerForm,
          isRegistration: true,
        });
      }

      setSuccess('Registration successful! Please log in.');
      setActiveTab(0); // Switch to login tab
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGuestLogin = async () => {
    if (!onGuestLogin) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await onGuestLogin();
    } catch (err) {
      setError(err.message || 'Guest login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={classes.authContainer}>
      <Paper className={classes.authPaper} elevation={0}>
        {/* Logo Section */}
        <div className={classes.logoSection}>
          <Avatar className={classes.logoIcon}>
            <VideoChatIcon />
          </Avatar>
          <Typography className={classes.logoText}>
            {tenantConfig?.tenant?.name || 'WebRTC Chat'}
          </Typography>
          {tenantConfig?.tenant?.description && (
            <Typography className={classes.logoSubtext}>
              {tenantConfig.tenant.description}
            </Typography>
          )}
        </div>

        {/* Tabs — only when there's a Sign Up to switch to */}
        {registrationEnabled && (
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="fullWidth"
            className={classes.tabs}
          >
            <Tab label="Sign In" className={classes.tab} />
            <Tab label="Sign Up" className={classes.tab} />
          </Tabs>
        )}

        {/* Error/Success Messages */}
        {error && (
          <Typography className={classes.errorText}>
            {error}
          </Typography>
        )}
        {success && (
          <Typography className={classes.successText}>
            {success}
          </Typography>
        )}

        {/* Login Form */}
        {!showRegister && (
          <form onSubmit={handleLogin}>
            <TextField
              className={classes.formField}
              label="Username"
              variant="outlined"
              fullWidth
              value={loginForm.username}
              onChange={handleLoginChange('username')}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon color="disabled" />
                    </InputAdornment>
                  ),
                },
              }}
            />

            <TextField
              className={classes.formField}
              label="Password"
              type={showPassword ? 'text' : 'password'}
              variant="outlined"
              fullWidth
              value={loginForm.password}
              onChange={handleLoginChange('password')}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon color="disabled" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={loginForm.rememberMe}
                    onChange={handleLoginChange('rememberMe')}
                    size="small"
                  />
                }
                label="Remember me"
                className={classes.checkboxLabel}
              />
              <Link className={classes.linkText}>
                Forgot password?
              </Link>
            </Box>

            <Button
              type="submit"
              variant="contained"
              className={classes.submitButton}
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
        )}

        {/* Register Form */}
        {showRegister && (
          <form onSubmit={handleRegister}>
            <TextField
              className={classes.formField}
              label="Username"
              variant="outlined"
              fullWidth
              value={registerForm.username}
              onChange={handleRegisterChange('username')}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon color="disabled" />
                    </InputAdornment>
                  ),
                },
              }}
            />

            <TextField
              className={classes.formField}
              label="Email (optional - for password recovery)"
              type="email"
              variant="outlined"
              fullWidth
              value={registerForm.email}
              onChange={handleRegisterChange('email')}
              placeholder="Enter email to enable password recovery"
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon color="disabled" />
                    </InputAdornment>
                  ),
                },
              }}
            />

            <TextField
              className={classes.formField}
              label="Password"
              type={showPassword ? 'text' : 'password'}
              variant="outlined"
              fullWidth
              value={registerForm.password}
              onChange={handleRegisterChange('password')}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon color="disabled" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />

            <TextField
              className={classes.formField}
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              variant="outlined"
              fullWidth
              value={registerForm.confirmPassword}
              onChange={handleRegisterChange('confirmPassword')}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon color="disabled" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                        size="small"
                      >
                        {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={registerForm.agreeToTerms}
                  onChange={handleRegisterChange('agreeToTerms')}
                  size="small"
                />
              }
              label={
                <span>
                  I agree to the{' '}
                  <Link className={classes.linkText}>Terms & Conditions</Link>
                  {' '}and{' '}
                  <Link className={classes.linkText}>Privacy Policy</Link>
                </span>
              }
              className={classes.checkboxLabel}
              style={{ marginBottom: 16 }}
            />

            <Button
              type="submit"
              variant="contained"
              className={classes.submitButton}
              disabled={loading}
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </Button>
          </form>
        )}

        {/* Guest Login */}
        {guestLoginEnabled && (
          <>
            <Divider className={classes.divider} />

            <Button
              variant="outlined"
              className={classes.guestButton}
              onClick={handleGuestLogin}
              disabled={loading}
            >
              Continue as Guest
            </Button>
          </>
        )}
      </Paper>
    </div>
  );
};

export default AuthScreen;